<template>
  <ul class="flex flex-col gap-16">
    <!-- eslint-disable tailwindcss/no-custom-classname -->
    <li
      v-for="(category, rowIndex) in displayRows"
      :key="category.title"
      class="fade-in-row"
      :style="{
        animationDelay: `${rowRevealDelayMs(rowIndex, displayRows.length)}ms`,
      }"
    >
      <!--
        Each row is a MarqueeRow component so it gets its own useMarqueeDrag
        instance and derives its scroll duration from its measured track
        width (useMarqueeDuration — exact px/s speeds). The track
        auto-scrolls via CSS @keyframes, pauses on pointer enter (JS), and
        can be dragged via pointer events to scrub back and forth. Keyboard
        focus pause is handled by focusin/focusout handlers (useMarqueePause,
        composed into useMarqueeDrag). Reduced-motion users get native
        overflow-x: auto scroll.
      -->
      <MarqueeRow
        :category
        :row-index
        :total-rows="displayRows.length"
        @click="(prompt: string) => $emit('click', prompt)"
      />
    </li>
    <!-- eslint-enable tailwindcss/no-custom-classname -->
  </ul>
</template>

<script lang="ts" setup>
import { computed, onMounted, onUnmounted, ref } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'

import translations from '../../ShoppingAssistant.translations'

import MarqueeRow from './components/MarqueeRow/MarqueeRow.vue'
import { useIsShoppingAssistantBlackFridayDealsEnabled } from './composables/useIsShoppingAssistantBlackFridayDealsEnabled/useIsShoppingAssistantBlackFridayDealsEnabled'
import type { MarqueeChip } from './types'

const props = defineProps<{
  categories: Array<{ prompts: string[]; title: string }>
}>()

defineEmits<{ click: [suggestion: string] }>()

const i18n = useI18n()
const isBlackFridayDealsEnabled =
  useIsShoppingAssistantBlackFridayDealsEnabled()

// Delay before the very first element starts revealing, so the reveal
// doesn't start while the incoming page is still settling.
const INITIAL_DELAY_MS = 200
// Gap between consecutive rows — rows reveal bottom-to-top (last row first).
const ROW_STAGGER_MS = 160

// Animation delay of a whole row in the bottom-to-top reveal.
function rowRevealDelayMs(rowIndex: number, totalRows: number): number {
  return INITIAL_DELAY_MS + (totalRows - 1 - rowIndex) * ROW_STAGGER_MS
}

// Estimated width of a single medium RevChip including the 8px gap.
// Used to calculate how many times prompts must be repeated so the
// marquee track is always wider than the viewport — even with only 2
// prompts the row stays full of pills with no empty gaps.
const ESTIMATED_CHIP_WIDTH_PX = 140
const CHIP_GAP_PX = 8

// --- Viewport-height-based row count ---
//
// The number of visible marquee rows depends on how much vertical space
// is available. When the on-screen keyboard opens (or the viewport is
// otherwise short), reducing rows prevents suggestions from obscuring
// the welcome message.
//
// Thresholds were derived from real measurements on a 375px-wide viewport:
//   header ≈ 64px, message+disclaimer ≈ 104px, content top padding ≈ 60px,
//   input pill ≈ 56px, aside bottom padding ≈ 24px (safe area),
//   IWS top/bottom padding ≈ 20px (8 + 12).
// Each marquee row is 40px + 16px gap.
//
//   3 rows need ≥ 152px of suggestion space → viewport ≥ ~520px
//   2 rows need ≥  96px of suggestion space → viewport ≥ ~460px
//   1 row needs  ≥  40px of suggestion space → viewport ≥ ~400px
//
// visualViewport.height is the visible area above the keyboard on iOS;
// window.innerHeight is the fallback when the API is unavailable.
// Keyboard presence is inferred from this height only: a keyboard that
// leaves 460px or more visible (e.g. landscape tablets) still shows 2 rows.
const ROW3_MIN_HEIGHT = 520
const ROW2_MIN_HEIGHT = 460

const availableHeight = ref<number | null>(null)
const viewportWidth = ref<number | null>(null)
let rafId: number | null = null

function syncHeight() {
  const vv = window.visualViewport
  availableHeight.value = vv ? vv.height : window.innerHeight
  viewportWidth.value = vv ? vv.width : window.innerWidth
}

function scheduleSyncHeight() {
  if (rafId !== null) return
  rafId = window.requestAnimationFrame(() => {
    rafId = null
    syncHeight()
  })
}

function maxRowsForHeight(height: number): number {
  if (height >= ROW3_MIN_HEIGHT) return 3
  if (height >= ROW2_MIN_HEIGHT) return 2

  return 1
}

// Before mount the height is unmeasured (null) — render every category
// (SSR / first paint) and clamp to the viewport state as soon as
// onMounted measures it.
const maxRows = computed(() => {
  const h = availableHeight.value
  if (h === null) return props.categories.length

  return Math.min(maxRowsForHeight(h), props.categories.length)
})

onMounted(() => {
  syncHeight()
  const vv = window.visualViewport
  if (vv) {
    vv.addEventListener('resize', scheduleSyncHeight)
    vv.addEventListener('scroll', scheduleSyncHeight)
  }
})

onUnmounted(() => {
  if (rafId !== null) {
    window.cancelAnimationFrame(rafId)
    rafId = null
  }
  const vv = window.visualViewport
  if (!vv) return
  vv.removeEventListener('resize', scheduleSyncHeight)
  vv.removeEventListener('scroll', scheduleSyncHeight)
})

// Distribute prompts across the available rows. When maxRows equals the
// number of categories, each category gets its own row. When fewer rows
// are available, categories are merged: later categories are flattened
// into earlier rows first, preserving a logical top-to-bottom order.
const displayRows = computed(() => {
  const cats = props.categories
  let rows: Array<{ prompts: string[]; title: string }>

  if (maxRows.value >= cats.length) {
    rows = cats
  } else if (maxRows.value <= 1) {
    rows = [
      {
        title: i18n(translations.promptSuggestionsTitle),
        prompts: cats.flatMap((cat) => cat.prompts),
      },
    ]
  } else {
    // 2 rows: merge the last categories into the second row.
    const firstRowCount = Math.ceil(cats.length / 2)
    const firstRowCats = cats.slice(0, firstRowCount)
    const secondRowCats = cats.slice(firstRowCount)

    rows = [
      {
        title: firstRowCats.map((c) => c.title).join(' / '),
        prompts: firstRowCats.flatMap((cat) => cat.prompts),
      },
      {
        title: secondRowCats.map((c) => c.title).join(' / '),
        prompts: secondRowCats.flatMap((cat) => cat.prompts),
      },
    ]
  }

  return rows.map((row, rowIndex) => {
    const isBlackFridayRow = rowIndex === 0 && isBlackFridayDealsEnabled.value
    const promptCount = row.prompts.length + (isBlackFridayRow ? 1 : 0)
    const width = viewportWidth.value ?? 375
    const estimatedOneSetWidth =
      promptCount * (ESTIMATED_CHIP_WIDTH_PX + CHIP_GAP_PX)
    const repeatCount = Math.max(
      1,
      Math.ceil(
        (width + ESTIMATED_CHIP_WIDTH_PX) / Math.max(1, estimatedOneSetWidth),
      ),
    )

    // Build the base set — prompts repeated enough times to fill the
    // viewport so the row is always full of pills. Only the first
    // repetition gets isDuplicate: false (for aria + fade-in animation).
    const baseChips: MarqueeChip[] = []
    for (let r = 0; r < repeatCount; r += 1) {
      for (let p = 0; p < row.prompts.length; p += 1) {
        const indexOffset = isBlackFridayRow && p >= 1 ? 1 : 0
        baseChips.push({
          prompt: row.prompts[p],
          index: p + indexOffset,
          isDuplicate: r > 0,
          copyIndex: r * promptCount + p + 1,
        })
        if (isBlackFridayRow && p === 0) {
          baseChips.push({
            prompt: i18n(translations.blackFridayDeals),
            index: 1,
            isDuplicate: r > 0,
            copyIndex: r * promptCount + p + 1,
            isBlackFriday: true,
          })
        }
      }
    }

    // Duplicate the entire base set for the seamless translateX(-50%) loop.
    const chips = [
      ...baseChips,
      ...baseChips.map((chip) => ({
        ...chip,
        isDuplicate: true,
        copyIndex: chip.copyIndex + baseChips.length,
      })),
    ]

    return { ...row, chips }
  })
})
</script>

<style scoped>
/* Rows fade in place — no translate. A vertical shift on this larger
   grouping read as too strong; only the chips themselves get a subtle
   individual lift, staggered more distinctly per chip. */
@keyframes fade-in-row {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.fade-in-row {
  animation: 250ms ease-out both fade-in-row;
}

@media (prefers-reduced-motion: reduce) {
  .fade-in-row {
    animation: none;
  }
}
</style>
